import { motion } from "framer-motion";
import { Mic, ListChecks, AlarmClock, CalendarDays, ClipboardList, UsersRound, Mail, Contact, BrainCircuit } from "lucide-react";
import { VoiceWave } from "./phone/PhoneFrame";

const FEATURES = [
  { id: "voice", icon: Mic, title: "Voice-first task management", body: "Say it once — GoDiGo captures, understands and files it. Hands-free productivity that actually works.", span: "md:col-span-2", wave: true },
  { id: "tasks", icon: ListChecks, title: "Tasks & To-Dos", body: "Lists, priorities and due dates organized automatically." },
  { id: "reminders", icon: AlarmClock, title: "Reminders & Alarms", body: "One-off or recurring nudges, right on time." },
  { id: "calendar", icon: CalendarDays, title: "Calendar & Events", body: "A living calendar synced with Google Calendar.", span: "md:col-span-2" },
  { id: "appointments", icon: ClipboardList, title: "Appointments", body: "Doctor, school, clients — booked and remembered." },
  { id: "assign", icon: UsersRound, title: "Assign responsibilities", body: "Delegate to family or teammates and track it." },
  { id: "email", icon: Mail, title: "Email integration", body: "Turn Gmail messages into actionable tasks." },
  { id: "contacts", icon: Contact, title: "Contacts", body: "Reach the right person from any task or event." },
  { id: "smart", icon: BrainCircuit, title: "Smart organization", body: "GoDiGo groups, prioritizes and surfaces what matters — so your day organizes itself.", span: "md:col-span-2" },
];

export default function Features() {
  return (
    <section id="features" className="py-20 lg:py-32" data-testid="features-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="max-w-2xl"
        >
          <p className="overline-label">Features</p>
          <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">
            Everything you juggle.<br />One assistant.
          </h2>
        </motion.div>

        <div className="mt-14 grid grid-cols-1 gap-5 md:grid-cols-3">
          {FEATURES.map((f, i) => (
            <motion.div
              key={f.id}
              data-testid={`feature-card-${f.id}`}
              initial={{ opacity: 0, y: 36 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.6, delay: (i % 3) * 0.08 }}
              className={`group relative overflow-hidden rounded-3xl border border-slate-200 bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:border-emerald-300 hover:shadow-[0_20px_48px_-20px_rgba(27,77,62,0.3)] ${f.span || ""}`}
            >
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#E8F3EE] text-[#1B4D3E] transition-colors duration-300 group-hover:bg-[#1B4D3E] group-hover:text-lime-300">
                <f.icon size={20} />
              </div>
              <h3 className="mt-5 font-heading text-lg font-bold text-slate-900">{f.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{f.body}</p>
              {f.wave && <VoiceWave bars={30} className="mt-5 justify-start opacity-60" />}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
